import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../lib/utils';

// Content type badge
export const ContentBadge = ({ type, className }) => {
  const styles = {
    quran: 'badge-quran',
    hadith: 'badge-hadith',
    tafsir: 'badge-tafsir',
    historical: 'badge-historical',
    scholarly: 'badge-scholarly',
    debated: 'bg-orange-900/50 text-orange-300 border-orange-700/50',
  };

  const labels = {
    quran: 'Coran',
    hadith: 'Hadith',
    tafsir: 'Tafsir',
    historical: 'Source historique',
    scholarly: 'Synthèse savante',
    debated: 'Rapport débattu',
  };

  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border',
      styles[type] || 'bg-gray-900/50 text-gray-300 border-gray-700/50',
      className
    )}>
      {labels[type] || type}
    </span>
  );
};

// Certainty level indicator
export const CertaintyBadge = ({ level, className }) => {
  const config = {
    established: { label: 'Établi', color: 'text-emerald-400 bg-emerald-900/30' },
    commonly_accepted: { label: 'Communément accepté', color: 'text-blue-400 bg-blue-900/30' },
    debated: { label: 'Débattu', color: 'text-orange-400 bg-orange-900/30' },
    weak: { label: 'Faible', color: 'text-red-400 bg-red-900/30' },
  };

  const { label, color } = config[level] || config.commonly_accepted;

  return (
    <span className={cn('text-xs px-2 py-1 rounded', color, className)}>
      {label}
    </span>
  );
};

// Hadith grade badge
export const HadithGradeBadge = ({ grade, className }) => {
  const config = {
    sahih: { label: 'Sahih', color: 'text-emerald-400 bg-emerald-900/30' },
    hasan: { label: 'Hasan', color: 'text-blue-400 bg-blue-900/30' },
    daif: { label: "Da'if", color: 'text-orange-400 bg-orange-900/30' },
    mawdu: { label: "Mawdu'", color: 'text-red-400 bg-red-900/30' },
  };

  const { label, color } = config[grade] || config.hasan;

  return (
    <span className={cn('text-xs px-2 py-1 rounded font-medium', color, className)}>
      {label}
    </span>
  );
};

// Arabic verse display (uses user-configurable font size via .quran-arabic-text)
export const ArabicVerse = ({ text, className }) => (
  <p className={cn(
    'quran-arabic-text text-text-primary',
    className
  )} dir="rtl">
    {text}
  </p>
);

// Section header
export const SectionHeader = ({ overline, title, subtitle, className }) => (
  <div className={cn('mb-8', className)}>
    {overline && (
      <span className="overline mb-2 block">{overline}</span>
    )}
    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-text-primary mb-2">
      {title}
    </h2>
    {subtitle && (
      <p className="text-text-secondary text-base max-w-2xl">{subtitle}</p>
    )}
  </div>
);

// Glass card
export const GlassCard = ({ children, className, ...props }) => (
  <div 
    className={cn('glass-card p-6', className)} 
    {...props}
  >
    {children}
  </div>
);

// Animated card for lists
export const AnimatedCard = ({ children, index = 0, className, ...props }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.05 }}
    className={cn('glass-card p-6 hover:border-primary/30 transition-all duration-300', className)}
    {...props}
  >
    {children}
  </motion.div>
);

// Source citation block
export const SourceCitation = ({ sources }) => {
  if (!sources || sources.length === 0) return null;
  
  return (
    <div className="mt-6 pt-4 border-t border-border">
      <h4 className="text-xs uppercase tracking-wider text-text-muted mb-2">Sources</h4>
      <ul className="space-y-1">
        {sources.map((source, i) => (
          <li key={i} className="text-sm text-text-secondary">
            <span className="text-text-muted">{source.name}</span>
            {source.author && <span className="text-text-muted"> — {source.author}</span>}
            {source.type && <span className="text-text-muted ml-2">({source.type})</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Timeline node
export const TimelineNode = ({ event, index, isLeft }) => (
  <motion.div
    initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, margin: '-100px' }}
    transition={{ duration: 0.5, delay: 0.1 }}
    className={cn(
      'relative flex items-center gap-8 py-8',
      isLeft ? 'flex-row' : 'flex-row-reverse'
    )}
  >
    {/* Content */}
    <div className={cn('flex-1', isLeft ? 'text-right' : 'text-left')}>
      <GlassCard className="inline-block max-w-md hover:border-primary/30 transition-all">
        <span className="overline">{event.date_hijri || event.date_gregorian}</span>
        <h3 className="font-serif text-xl text-text-primary mt-2">{event.title_french || event.title_english}</h3>
        <p className="font-arabic text-lg text-primary mt-1" dir="rtl">{event.title_arabic}</p>
        <p className="text-sm text-text-secondary mt-3 line-clamp-3">{event.narrative}</p>
        {event.location && (
          <p className="text-xs text-text-muted mt-2">📍 {event.location}</p>
        )}
      </GlassCard>
    </div>

    {/* Node */}
    <div className="relative z-10">
      <div className="w-4 h-4 rounded-full bg-primary border-4 border-background" />
    </div>

    {/* Spacer */}
    <div className="flex-1" />
  </motion.div>
);

// Loading skeleton
export const Skeleton = ({ className }) => (
  <div className={cn('animate-pulse bg-surface-elevated rounded', className)} />
);

// Page loading
export const PageLoading = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto mb-4" />
      <p className="text-text-muted">Chargement...</p>
    </div>
  </div>
);

// Empty state
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="text-center py-16">
    {Icon && <Icon className="w-16 h-16 text-text-muted mx-auto mb-4" />}
    <h3 className="text-lg font-medium text-text-primary mb-2">{title}</h3>
    {description && <p className="text-text-secondary mb-4">{description}</p>}
    {action}
  </div>
);
